import { useEffect, useState } from 'react';
import { BarChart3, Eye, MousePointerClick, Send, Users } from 'lucide-react';
import { fetchAnalyticsSummary } from '../lib/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border border-white/10 bg-white/5 p-6">
    <div className="mb-4 flex h-11 w-11 items-center justify-center bg-[#1E40AF] text-white">
      <Icon className="h-5 w-5" />
    </div>
    <div className="font-['JetBrains_Mono'] text-3xl font-extrabold text-[#93C5FD]">{value}</div>
    <div className="mt-2 text-sm font-semibold uppercase tracking-wider text-slate-300">{label}</div>
  </div>
);

const List = ({ title, items }) => (
  <div className="border border-white/10 bg-white/5 p-6">
    <h2 className="mb-5 text-lg font-bold uppercase tracking-wider text-white">{title}</h2>
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">No data yet.</p>
      ) : (
        items.map((item) => (
          <div key={`${title}-${item.name}`} className="grid grid-cols-[1fr_auto] items-center gap-4 text-sm">
            <span className="truncate text-slate-300">{item.name}</span>
            <span className="font-['JetBrains_Mono'] font-bold text-[#93C5FD]">{item.count}</span>
          </div>
        ))
      )}
    </div>
  </div>
);

const AnalyticsPage = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [token, setToken] = useState(() => window.localStorage.getItem('leion_analytics_token') || '');
  const [isUnlocked, setIsUnlocked] = useState(() => Boolean(window.localStorage.getItem('leion_analytics_token')));

  const loadSummary = () => {
    setError('');
    fetchAnalyticsSummary(30)
      .then(setSummary)
      .catch((requestError) => {
        setSummary(null);
        if ([401, 403].includes(requestError?.response?.status)) {
          setIsUnlocked(false);
          window.localStorage.removeItem('leion_analytics_token');
          setError('Analytics is locked. Enter the correct admin token.');
          return;
        }
        setError('Analytics is unavailable right now. Please try again shortly.');
      });
  };

  useEffect(() => {
    if (isUnlocked) loadSummary();
  }, [isUnlocked]);

  const handleUnlock = (event) => {
    event.preventDefault();
    window.localStorage.setItem('leion_analytics_token', token.trim());
    setIsUnlocked(true);
  };

  const totals = summary?.totals || {};

  return (
    <div className="tech-grid min-h-screen bg-[#071327] py-16" data-testid="analytics-page">
      <Seo {...routeMeta['/leion-admin-insights']} path="/leion-admin-insights" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="section-eyebrow">Website Analytics</span>
          <h1 className="font-['Manrope'] mt-4 text-4xl font-extrabold uppercase tracking-tight text-white md:text-5xl">
            Visitor and CTA Performance
          </h1>
          <p className="mt-4 max-w-2xl text-slate-300">
            Last {summary?.days || 30} days of first-party website activity.
          </p>
        </div>

        {error && (
          <div className="mb-6 border border-red-400/30 bg-red-950/30 p-4 text-sm text-red-100">
            {error}
          </div>
        )}

        {!isUnlocked && (
          <form onSubmit={handleUnlock} className="max-w-xl border border-white/10 bg-white/5 p-6">
            <label htmlFor="analytics-token" className="text-sm font-bold uppercase tracking-wider text-white">
              Admin Token
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <Input
                id="analytics-token"
                type="password"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                className="rounded-sm border-slate-300 bg-slate-50"
                placeholder="Enter analytics token"
                required
              />
              <Button type="submit" className="rounded-sm bg-[#1E40AF] px-6 font-bold uppercase tracking-wider text-white hover:bg-[#2563EB]">
                Unlock
              </Button>
            </div>
          </form>
        )}

        {isUnlocked && (
          <>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Eye} label="Page Visits" value={totals.visits || 0} />
          <Stat icon={Users} label="Unique Visitors" value={totals.unique_visitors || 0} />
          <Stat icon={MousePointerClick} label="Button Clicks" value={totals.button_clicks || 0} />
          <Stat icon={Send} label="Quote Requests" value={totals.quote_requests_completed || 0} />
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-3">
          <Stat icon={BarChart3} label="Quote Starts" value={totals.quote_requests_started || 0} />
          <Stat icon={Send} label="Email Fallbacks" value={totals.quote_email_fallbacks || 0} />
          <Stat icon={MousePointerClick} label="Contact Clicks" value={totals.contact_clicks || 0} />
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          <List title="Top Pages" items={summary?.top_pages || []} />
          <List title="Top Buttons" items={summary?.top_buttons || []} />
          <List title="Devices" items={summary?.top_devices || []} />
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
